// TODO: we disable this because the unbound-method is a very frequent pattern for class-based designs
// TODO: we addEventListener and then we need to removeEventListener, we cannot use arrow functions there

import { Application } from 'pixi.js';
import { AbortError, wait, waitForSeconds } from '@tools/async/awaitable';
import AbortPlugin from '@tools/async/gsap-abort-plugin';
import { initializePixiInspector } from '@tools/pixi/pixi-inspector';
import { DisplayScaler } from '@tools/pixi/display-scaler';
import { Disposable } from '@tools/interfaces/disposable';
import gsap from 'gsap';
import { isIpad, isSafariWebView } from '@tools/browsers';
import { assets, loadAssetBundles } from '@tools/asset-utility';
import spineAtlas from './assets/spine/avatar.atlas.ts';
import spineJson from './assets/spine/avatar.json';
import spineImage from './assets/spine/avatar.png';
import { Spine } from '@pixi-spine/all-4.1';
import { createSpine } from '@tools/pixi/pixijs-spine-utils';
import { RoleId } from './role-id.ts';
import randomInt from '@tools/math/random.ts';

/* eslint-disable @typescript-eslint/unbound-method */
gsap.registerPlugin(AbortPlugin);

@assets(
  'HomePixiView',
  {
    spineImage,
  },
  []
)
export class HomePixiView implements Disposable {
  application: Application;
  private disposed = false;
  private displayScaler: DisplayScaler;
  protected body!: Spine;
  private abort = new AbortController();

  constructor(container: HTMLElement) {
    this.application = new Application({
      resizeTo: container,
      autoDensity: true,
      antialias: true,
      backgroundAlpha: 0,
    });

    container.appendChild(this.application.view as any);

    initializePixiInspector(this.application);
    this.displayScaler = new DisplayScaler(this.application.stage, { x: 0, y: 0, width: 1024, height: 768 }, 0.5);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  async initialize(progression: (progress: number) => void = () => {}) {
    if (this.disposed) throw new Error('duck-fraction-activity is already disposed');
    await loadAssetBundles([HomePixiView], progression);

    const stage = this.application.stage;
    this.body = createSpine(spineAtlas, spineJson, spineImage);
    stage.addChild(this.body);

    this.switchSkin('dev');
    this.onResize();
  }

  public switchSkin(id: RoleId) {
    this.refreshAbort();
    this.body.state.setEmptyAnimation(0);
    this.body.state.setEmptyAnimation(1);
    this.body.state.setEmptyAnimation(2);

    const skeleton = this.body.skeleton;
    skeleton.setSkinByName(id);
    skeleton.setSlotsToSetupPose();

    switch (id) {
      case 'dev':
      case 'game-designer':
        void this.tapKeyboardAnimation();
        break;
      case 'animator':
        void this.tapKeyboardAnimation();
        this.playAnimation('animator/emoji-dance', true, 2);
        break;
      case 'artist':
        void this.drawAnimation();
        break;
      case 'cooker':
        void this.cookAnimation();
        break;
    }
  }

  private refreshAbort() {
    this.abort.abort(new AbortError('refreshAbort'));
    this.abort = new AbortController();
  }

  private playAnimation(animation: string, loop = false, index = 0, speed = 1) {
    this.body.state.setAnimation(index, animation, loop);
    this.body.state.timeScale = speed;
  }

  private async tapKeyboardAnimation() {
    const signal = this.abort.signal;
    this.playAnimation('tap-keyboard', true);
    while (!signal.aborted) {
      await waitForSeconds(this.body.skeleton.data.findAnimation('tap-keyboard').duration * randomInt(5, 10), signal);
      this.playAnimation('drink-coffe', false, 1);
      await waitForSeconds(this.body.skeleton.data.findAnimation('drink-coffe').duration, signal);
      await wait(200, signal);
    }
  }

  private async drawAnimation() {
    const signal = this.abort.signal;
    while (!signal.aborted) {
      this.playAnimation('artist/draw', true);
      await waitForSeconds(this.body.skeleton.data.findAnimation('artist/draw').duration * randomInt(4, 8), signal);
      this.body.state.clearTrack(0);
      this.playAnimation('drink-coffe');
      await waitForSeconds(this.body.skeleton.data.findAnimation('drink-coffe').duration, signal);
      await wait(200, signal);
    }
  }

  private async cookAnimation() {
    const signal = this.abort.signal;
    while (!signal.aborted) {
      this.playAnimation('cooker/cook', true);
      await waitForSeconds(this.body.skeleton.data.findAnimation('cooker/cook').duration * randomInt(5, 8), signal);
      this.playAnimation('cooker/salt');
      await waitForSeconds(this.body.skeleton.data.findAnimation('cooker/salt').duration, signal);
    }
  }

  dispose() {
    window.removeEventListener('resize', this.onResize);
    this.abort.abort(new AbortError('dispose duck-fraction-activity'));
    this.disposed = true;
    this.application.destroy(true, {
      children: true,
    });
    this.displayScaler.dispose();
  }

  onVisibilityChange = () => {
    if (document.visibilityState === 'visible') void this.onResize();
  };

  onResize = async () => {
    if (!this.application || this.abort.signal.aborted) return;
    if (isSafariWebView() || isIpad()) await waitForSeconds(0.8, this.abort.signal); // Hack for webview. Safari webview sends inconsistent dimensions on resize on slower devices.
    this.application.resize();
    const screen = this.application.screen;
    this.displayScaler.resize(screen);
    this.body.position.set(screen.width / 2.2, screen.height / 1.95);
    this.body.scale.set(this.displayScaler.scaleFactor * 0.67);
  };
}
